import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrimeTemplate } from 'primeng/api';
import { finalize } from 'rxjs';

import { KanbanServicosService } from '@/core/api/sicom-api/servicos/kanban/kanban-servicos.service';
import { LoggerService } from '@/core/services/logger.service';
import { mapearItemStatusProtocolo, obterTextoResponse } from '@/features/servicos/shared/dashboard-servicos/dashboard-servicos.mapper';
import { type TipoServicoPessoa } from '@/features/servicos/shared/enums/servico-disponivel.enum';
import type { ItemStatus } from '@/features/servicos/shared/models/item-status.model';
import { type CrmUf, separarCrmUf } from '@/features/servicos/shared/utils/crm.utils';
import { formatarDataSomenteData } from '@/features/servicos/shared/utils/protocolo-rotulos.utils';
import { crmUfDoRostoDoCard, rostoDoCardEhDoDiretorTecnico } from '@/features/servicos/shared/utils/servicos-acoes.utils';
import { CustomTableComponent, type TableMetadata, type TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { IdentificacaoPessoa } from '@/shared/components/pessoa/identificacao-pessoa/identificacao-pessoa';
import { FotosMedicosService, iniciaisDoNome } from '@/shared/services/fotos-medicos.service';

/**
 * Tamanho inicial de página, e piso de fallback quando o evento do paginador não informa `rows`. Não
 * é fixo: o `app-custom-table` expõe o seletor "linhas por página" (10/15/20), e o tamanho realmente
 * pedido ao backend a cada chamada vive em `linhasPorPagina`.
 */
const LINHAS_POR_PAGINA = 20;

/**
 * Linha da tabela: o card já mapeado pelo mapper do quadro, mais os três campos que só a tabela de
 * controle sabe (abertura, conclusão, responsável). O portal não os devolve, então eles não fazem
 * parte de `ItemStatus` — entram por fora, no `mapearLinha` desta classe.
 */
type LinhaArquivada = ItemStatus & {
    dataAbertura: string;
    dataConclusao: string;
    responsavel: string;
    /** Id de quem atendeu; é a chave da foto, e vem só da tabela de controle. */
    idWebUsuarioResponsavel?: number;
};

/**
 * A linha como a TABELA a consome: a linha crua mais os campos que dependem de foto. Tipo à parte
 * porque estes não nascem em `mapearLinha` — ver o `computed` `linhas()`.
 */
type LinhaDesenhada = LinhaArquivada & {
    solicitantePerfil: TablePerfilValue;
    rotuloRetrato: string;
    inscricao: string;
    responsavelPerfil: TablePerfilValue;
};

/**
 * Lista dos protocolos ARQUIVADOS — concluídos que saíram da janela de 30 dias do quadro e, até
 * aqui, não tinham onde ser consultados.
 *
 * Pagina no servidor de propósito: o arquivo cresce para sempre e cada linha exibida custa uma
 * consulta ao portal, então trazer tudo degradaria com o tempo.
 *
 * Fronteira deliberada: este componente só lista. O resumo do topo (total, concluídos no ano,
 * tempo médio) é buscado e desenhado por quem o incorpora — quem mostra o número busca o número.
 */
@Component({
    selector: 'app-arquivados-protocolos',
    standalone: true,
    imports: [CustomTableComponent, PrimeTemplate, IdentificacaoPessoa],
    providers: [FotosMedicosService],
    templateUrl: './arquivados-protocolos.html',
    styleUrl: './arquivados-protocolos.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArquivadosProtocolos {
    private readonly kanbanServicosService = inject(KanbanServicosService);
    private readonly logger = inject(LoggerService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly fotosMedicos = inject(FotosMedicosService);

    /**
     * Tamanho de página EFETIVAMENTE pedido ao backend, escolhido no seletor do `app-custom-table`.
     * Signal, e não a constante: o rodapé do paginador anuncia a faixa a partir do `rows`/`first`
     * que ele mesmo controla, então o tamanho que este componente pede tem de ser o MESMO, ou a
     * faixa anunciada ("16 a 30") diverge das linhas que de fato chegam na tela.
     */
    readonly linhasPorPagina = signal(LINHAS_POR_PAGINA);

    /**
     * Página pedida na chamada em curso (não a exibida — `primeiraLinha` é isso). Existe só para o
     * guard `aindaCorrente` de `carregarPagina`: sem saber qual página ESTA resposta pediu, uma
     * resposta de página antiga que chegue depois de uma mais nova a sobrescreveria.
     */
    private readonly paginaAtual = signal(0);

    /** Serviço cujo arquivo é listado; sem ele (ou sem `uf`) não há o que carregar. */
    readonly idServico = input<number | undefined>(undefined);
    readonly uf = input('');
    /** Número do protocolo buscado na barra; `undefined` lista o arquivo inteiro. */
    readonly numeroBusca = input<number | undefined>(undefined);

    /**
     * Quem a linha retrata e qual CRM ela exibe. Em pessoa jurídica o rosto é do diretor técnico e
     * o CRM é o da EMPRESA — as duas coisas vêm de campos diferentes do mesmo item, e resolver isso
     * aqui em vez de no template é o que permite testá-lo.
     */
    readonly tipoPessoa = input<TipoServicoPessoa>('pessoa-fisica');

    /**
     * Emitido ao escolher uma linha. Quem incorpora decide como abrir o detalhe — o MESMO caminho
     * do card do quadro, e não uma segunda porta para a mesma tela.
     */
    readonly protocoloSelecionado = output<ItemStatus>();

    readonly itens = signal<LinhaArquivada[]>([]);
    readonly total = signal(0);
    readonly primeiraLinha = signal(0);
    readonly carregando = signal(false);
    readonly erro = signal<string | null>(null);

    /**
     * Retratos de quem atendeu, por id. O backend só devolve quem TEM foto — ausência aqui é
     * ausência de verdade, não erro de carga, e a coluna cai nas iniciais.
     */
    private readonly fotosResponsaveis = signal<Map<number, string>>(new Map());

    /** Vazio só depois de carregar sem erro: antes disso o estado é "carregando", e a tela precisa distinguir os dois. */
    readonly vazio = computed(() => !this.carregando() && this.erro() === null && this.itens().length === 0);

    /**
     * O que a tabela desenha. Derivado, e não gravado em `mapearLinha`: as fotos chegam depois da
     * resposta, e um retrato gravado na linha ficaria congelado no valor que existia no instante em
     * que ela foi montada — a foto que chegasse depois não redesenharia nada.
     */
    readonly linhas = computed<LinhaDesenhada[]>(() =>
        this.itens().map((item) => {
            const ehPj = rostoDoCardEhDoDiretorTecnico(this.tipoPessoa());
            const crmUfDoRosto = separarCrmUf(crmUfDoRostoDoCard(this.tipoPessoa(), item), item.ufCrm);
            // O nome do RETRATADO, que não é o nome exibido em pessoa jurídica: um círculo com as
            // iniciais de uma empresa sugere uma pessoa que não existe.
            const nomeRetratado = ehPj ? item.diretorTecnicoNome : item.empresa;
            const inscricao = separarCrmUf(item.crmUf, item.ufCrm);

            return {
                ...item,
                solicitantePerfil: {
                    nome: item.empresa,
                    foto: crmUfDoRosto ? this.fotosMedicos.fotoPorCrmUf(String(crmUfDoRosto.crm), crmUfDoRosto.uf) : null,
                    iniciais: iniciaisDoNome(nomeRetratado) || null
                },
                // Vazio em pessoa física mantém o retrato decorativo: ali ele mostra exatamente o
                // nome que já se lê ao lado.
                rotuloRetrato: ehPj && item.diretorTecnicoNome ? `Diretor técnico: ${item.diretorTecnicoNome}` : '',
                inscricao: inscricao ? `CRM ${inscricao.crm}/${inscricao.uf.toUpperCase()}` : '',
                responsavelPerfil: {
                    nome: item.responsavel,
                    foto: item.idWebUsuarioResponsavel == null ? null : (this.fotosResponsaveis().get(item.idWebUsuarioResponsavel) ?? null),
                    iniciais: iniciaisDoNome(item.responsavel) || null
                }
            };
        })
    );

    readonly metadataTabela: TableMetadata = {
        title: 'Protocolos arquivados',
        headers: {
            // `type: 'perfil'` mesmo com a célula sob medida: o valor É um `TablePerfilValue`, e
            // declarar 'text' aqui só mentiria. Se um dia o `pTemplate` sair, a degradação é
            // retrato+nome — com 'text' seria `[object Object]` na tela.
            solicitantePerfil: { type: 'perfil', value: 'Solicitante', hidden: false, default: 'Não informado', celulaCustomizada: true },
            dataAbertura: {
                type: 'text',
                value: 'Aberto e concluído',
                hidden: false,
                default: '-',
                enfase: { forte: true, maquina: true },
                sub: { key: 'dataConclusao', value: 'Concluído em', default: '-', enfase: { maquina: true } }
            },
            responsavelPerfil: { type: 'perfil', value: 'Responsável', hidden: false, default: 'Não atribuído' }
        }
    };

    constructor() {
        effect(() => {
            const idServico = this.idServico();
            const uf = this.uf();
            // Lido só para entrar como dependência do efeito: uma busca nova também reabre na
            // primeira página, como troca de serviço/UF.
            this.numeroBusca();
            // `tipoPessoa` NÃO entra, de propósito: trocá-lo sozinho recomputaria `linhas()` pedindo
            // retratos de CRMs que nunca foram buscados, e as linhas ficariam nas iniciais até a
            // próxima carga. Na prática natureza e serviço mudam juntos — e foto é enfeite.
            if (idServico == null || !uf) {
                return;
            }
            this.primeiraLinha.set(0);
            // `carregarPagina` lê `linhasPorPagina()` internamente; sem `untracked`, essa leitura
            // entraria como dependência DESTE efeito. `aoTrocarPagina` escreve em `linhasPorPagina`
            // para pedir o tamanho novo — sem o `untracked`, essa escrita reagendava o efeito, que
            // rodava de novo, zerava `primeiraLinha` e pedia a página 0, desfazendo a página que
            // `aoTrocarPagina` tinha acabado de calcular. Este efeito só deve reabrir a paginação
            // por causa de serviço/UF/busca — nunca por causa do tamanho de página escolhido.
            untracked(() => this.carregarPagina(0));
        });
    }

    /**
     * `evento.rows` é o tamanho que o analista ESCOLHEU no seletor do `app-custom-table` (10/15/20),
     * não necessariamente o que já estava em uso. Ele é gravado em `linhasPorPagina` ANTES de
     * calcular a página — calcular com um tamanho e pedir ao backend outro é exatamente o defeito
     * relatado: o rodapé anuncia uma faixa e a tela mostra outra.
     */
    aoTrocarPagina(evento: { first: number; rows: number }): void {
        const tamanho = evento.rows || LINHAS_POR_PAGINA;
        this.linhasPorPagina.set(tamanho);
        this.primeiraLinha.set(evento.first);
        this.carregarPagina(Math.floor(evento.first / tamanho));
    }

    /**
     * Abre o detalhe da linha escolhida. `null` chega quando o clique é no MESMO item já
     * selecionado (o `app-custom-table` desmarca em vez de selecionar de novo): não há o que abrir,
     * então ignora.
     */
    aoSelecionar(item: ItemStatus | null): void {
        if (item) {
            this.protocoloSelecionado.emit(item);
        }
    }

    private carregarPagina(pagina: number): void {
        const idServico = this.idServico();
        const uf = this.uf();
        const numeroBusca = this.numeroBusca();
        const tamanho = this.linhasPorPagina();
        if (idServico == null || !uf) {
            return;
        }

        // Fixa a página desta chamada como a "pedida agora": é o que o guard abaixo compara quando a
        // resposta (desta chamada ou de uma seguinte) chegar.
        this.paginaAtual.set(pagina);

        // "Última pedida é a que fica": além de serviço/UF/busca, também página e tamanho — sem os
        // dois, duas páginas pedidas em sequência podiam responder fora de ordem e a resposta da
        // página MAIS VELHA sobrescrever a da mais nova (o paginador aponta uma página, a tabela
        // mostra outra).
        const aindaCorrente = (): boolean => this.idServico() === idServico && this.uf() === uf && this.numeroBusca() === numeroBusca && this.linhasPorPagina() === tamanho && this.paginaAtual() === pagina;

        this.carregando.set(true);
        this.erro.set(null);
        this.kanbanServicosService
            .listarArquivadas(idServico, uf, pagina, tamanho, numeroBusca)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    if (aindaCorrente()) {
                        this.carregando.set(false);
                    }
                })
            )
            .subscribe({
                next: (resposta) => {
                    if (!aindaCorrente()) {
                        return;
                    }
                    const linhas = resposta.itens.map((item, indice) => this.mapearLinha(item, indice, uf));
                    this.total.set(resposta.total);
                    this.itens.set(linhas);
                    // Depois de `itens.set`: a tabela já desenha, e os retratos entram quando
                    // chegarem. Falha aqui não é tratada — o `FotosMedicosService` já a contém, e a
                    // linha cai nas iniciais.
                    this.fotosMedicos.carregar(
                        linhas
                            .map((linha) => separarCrmUf(crmUfDoRostoDoCard(this.tipoPessoa(), linha), linha.ufCrm))
                            .filter((crmUf): crmUf is CrmUf => crmUf !== null)
                            .map((crmUf) => ({ crm: String(crmUf.crm), uf: crmUf.uf }))
                    );
                    this.carregarFotosDosResponsaveis(linhas, aindaCorrente);
                },
                error: (falha: unknown) => {
                    if (!aindaCorrente()) {
                        return;
                    }
                    this.itens.set([]);
                    this.total.set(0);
                    this.logger.error('Falha ao carregar o arquivo de protocolos:', falha);
                    this.erro.set('Não foi possível carregar os protocolos arquivados.');
                }
            });
    }

    /**
     * `mapearItemStatusProtocolo` cobre protocolo e solicitante — o MESMO mapper do quadro, para o
     * card não ter uma segunda leitura que possa divergir dele. Abertura, conclusão e responsável são
     * dados só da tabela de controle (`dtAbertura`/`dtConclusao`/`nomeResponsavel` no item cru); a
     * leitura usa o mesmo utilitário tolerante a alias do mapper (`obterTextoResponse`), em vez de
     * uma segunda regra de parsing, e cai no `default: '-'` do metadata quando o protocolo não tem
     * responsável atribuído (backlog) ou a data faltar.
     */
    private mapearLinha(bruto: Record<string, unknown>, indice: number, uf: string): LinhaArquivada {
        return {
            ...mapearItemStatusProtocolo(bruto, indice, uf),
            // dtAbertura/dtConclusao chegam como LocalDate (só-data): formatarDataSomenteData, não
            // formatarDataTramitacao — esta última passa por `Date` e desloca o dia pelo fuso (ver
            // doc-comment da função).
            dataAbertura: formatarDataSomenteData(obterTextoResponse(bruto, ['dtAbertura', 'dataAbertura', 'dt_abertura'])),
            dataConclusao: formatarDataSomenteData(obterTextoResponse(bruto, ['dtConclusao', 'dataConclusao', 'dt_conclusao'])),
            responsavel: obterTextoResponse(bruto, ['nomeResponsavel', 'responsavel', 'nome_responsavel']) ?? '',
            // Sem `obterNumeroResponse` no mapper: o número sai do MESMO leitor tolerante a alias, e
            // `|| undefined` cobre tanto a ausência quanto um valor ilegível (NaN).
            idWebUsuarioResponsavel: Number(obterTextoResponse(bruto, ['idWebUsuarioResponsavel', 'id_web_usuario_responsavel'])) || undefined
        };
    }

    /**
     * Retratos de quem atendeu, numa requisição só pelos ids distintos da página — `listarFotos-
     * Responsaveis` recebe a lista inteira, ao contrário da biometria, que não tem versão em lote.
     *
     * `aindaCorrente` é o MESMO guard da listagem: sem ele, a resposta de uma página já abandonada
     * escreveria as fotos dela sobre as linhas da página em tela. Falha vira log — foto é enfeite.
     */
    private carregarFotosDosResponsaveis(linhas: LinhaArquivada[], aindaCorrente: () => boolean): void {
        const ids = [...new Set(linhas.map((linha) => linha.idWebUsuarioResponsavel).filter((id): id is number => id != null))];
        if (!ids.length) {
            this.fotosResponsaveis.set(new Map());
            return;
        }

        this.kanbanServicosService
            .listarFotosResponsaveis(ids)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (fotos) => {
                    if (!aindaCorrente()) {
                        return;
                    }
                    // Substitui, não mescla: a página trocou, e guardar as fotos da anterior só
                    // acumularia imagens de linhas que ninguém mais vê.
                    this.fotosResponsaveis.set(new Map(fotos.map((foto) => [foto.idWebUsuario, `data:image/jpeg;base64,${foto.fotoBase64}`])));
                },
                error: (falha: unknown) => this.logger.error('Falha ao carregar as fotos dos responsáveis:', falha)
            });
    }
}
